import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, map, catchError, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { FcmService } from './fcm.service';
import { DISCIPLINE_TAB_STORAGE_KEY, SELECTED_DOMAINS_STORAGE_KEY } from './toolkit-storage-keys';

export interface UserInfo {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  type: string;
  tenant: string;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
}

interface RefreshTokenResponse {
  access_token: string;
  refresh_token: string;
}

interface SSOResponse {
  access_token: string;
  refresh_token: string;
  created: boolean;
}

interface RegisterResponse {
  access_token: string;
  refresh_token: string;
}

export interface RegisterPayload {
  first_name: string;
  last_name?: string;
  email: string;
  password: string;
  phone?: string;
}

interface RegisterCompanyResponse {
  access_token: string;
  refresh_token: string;
  message: string;
}

interface MessageResponse {
  message: string;
}

export interface DomainExpertRegistrationPayload {
  // Personal
  first_name: string;
  last_name: string;
  country_code: string;
  contact_number: string;
  email: string;
  password: string;
  // Professional
  core_expertise: string;
  primary_industry: string;
  current_status: string;
  companies_worked: string[];
  hourly_rate: string;
  linkedin_url: string;
}

export interface CompanyRegistrationPayload {
  // Account
  email: string;
  password: string;
  // Company details
  company_name: string;
  company_type: string;
  sector: string;
  country_code: string;
  contact_number: string;
  website_url: string;
  street_address: string;
  city: string;
  state: string;
  country: string;
  total_employees: string;
  yearly_turnover: string;
  // Documents
  gst_number: string;
  company_cin: string;
  incorporation_date: string;
  gst_certificate?: File;
  cin_file?: File;
  aadhar_file?: File;
}

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user_info';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private fcm = inject(FcmService);

  get accessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  get refreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  get user(): UserInfo | null {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  }

  isAuthenticated(): boolean {
    return !!this.accessToken && !this.isTokenExpired(this.accessToken);
  }

  hasValidRefreshToken(): boolean {
    return !!this.refreshToken && !this.isTokenExpired(this.refreshToken);
  }

  isDigilabsUser(): boolean {
    return !!this.user?.tenant;
  }

  /** Exchanges the stored refresh token for a new access/refresh token pair. */
  refreshAccessToken(): Observable<boolean> {
    const refreshToken = this.refreshToken;
    if (!refreshToken) {
      return of(false);
    }

    return this.http
      .post<RefreshTokenResponse>(`${environment.apiUrl}/api/auth/refresh/`, {
        refresh_token: refreshToken,
      })
      .pipe(
        tap(({ access_token, refresh_token }) => {
          localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
          localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
          const userInfo = this.decodeUserFromToken(access_token);
          localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
          this.fcm.register(access_token);
        }),
        map(() => true),
        catchError(() => {
          this.clearSession();
          return of(false);
        }),
      );
  }

  login(email: string, password: string): Observable<UserInfo> {
    return this.http
      .post<LoginResponse>(`${environment.apiUrl}/api/auth/login/`, { email, password })
      .pipe(
        tap(({ access_token, refresh_token }) => {
          localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
          localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
          const userInfo = this.decodeUserFromToken(access_token);
          localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
          this.fcm.register(access_token);
        }),
        map(({ access_token }) => this.decodeUserFromToken(access_token)),
      );
  }

  register(payload: RegisterPayload): Observable<UserInfo> {
    return this.http
      .post<RegisterResponse>(`${environment.apiUrl}/api/auth/register/`, payload)
      .pipe(
        tap(({ access_token, refresh_token }) => {
          localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
          localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
          const userInfo = this.decodeUserFromToken(access_token);
          localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
          this.fcm.register(access_token);
        }),
        map(({ access_token }) => this.decodeUserFromToken(access_token)),
      );
  }

  loginWithRefreshToken(digilabsRefreshToken: string, tenant: string): Observable<UserInfo> {
    return this.http
      .post<SSOResponse>(`${environment.apiUrl}/api/auth/sso/`, {
        refresh_token: digilabsRefreshToken,
        tenant,
      })
      .pipe(
        tap(({ access_token, refresh_token }) => {
          localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
          localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);

          const userInfo = this.decodeUserFromToken(access_token);
          localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
          this.fcm.register(access_token);
        }),
        map(({ access_token }) => this.decodeUserFromToken(access_token)),
      );
  }

  loginWithVivaQuestToken(vivaQuestRefreshToken: string): Observable<UserInfo> {
    return this.http
      .post<SSOResponse>(`${environment.apiUrl}/api/auth/viva-sso/`, {
        refresh_token: vivaQuestRefreshToken,
      })
      .pipe(
        tap(({ access_token, refresh_token }) => {
          localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
          localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);

          const userInfo = this.decodeUserFromToken(access_token);
          localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
          this.fcm.register(access_token);
        }),
        map(({ access_token }) => this.decodeUserFromToken(access_token)),
      );
  }

  registerDomainExpert(payload: DomainExpertRegistrationPayload): Observable<RegisterCompanyResponse> {
    return this.http.post<RegisterCompanyResponse>(
      `${environment.apiUrl}/api/auth/register/domain-expert/`,
      payload,
    );
  }

  registerCompany(payload: CompanyRegistrationPayload): Observable<RegisterCompanyResponse> {
    const form = new FormData();
    (Object.keys(payload) as (keyof CompanyRegistrationPayload)[]).forEach((key) => {
      const value = payload[key];
      if (value === undefined || value === null) {
        return;
      }
      form.append(key, value as string | Blob);
    });
    return this.http.post<RegisterCompanyResponse>(
      `${environment.apiUrl}/api/auth/register/company/`,
      form,
    );
  }

  forgotPassword(email: string): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(`${environment.apiUrl}/api/auth/forgot-password/`, { email });
  }

  resetPassword(token: string, newPassword: string, confirmPassword: string): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(`${environment.apiUrl}/api/auth/reset-password/`, {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
  }

  getDigilabsUrl(): Observable<string> {
    return this.http
      .get<{ url: string }>(`${environment.apiUrl}/api/labs/digilabs-url/`, {
        headers: { Authorization: `Bearer ${this.accessToken}` },
      })
      .pipe(map(({ url }) => url));
  }

  logout(): void {
    const accessToken = this.accessToken;
    if (accessToken) {
      this.fcm.unregister(accessToken);
      // Best-effort: revoke the session server-side so the paired refresh
      // token can't be replayed. The local session is cleared regardless of
      // whether this call succeeds (e.g. the access token already expired).
      this.http
        .post(`${environment.apiUrl}/api/auth/logout/`, {}, { headers: { Authorization: `Bearer ${accessToken}` } })
        .pipe(catchError(() => of(null)))
        .subscribe();
    }
    this.clearSession();
    this.router.navigate(['/login']);
  }

  private clearSession(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    // Otherwise, on a shared/lab computer, the next student to log in here
    // would inherit whichever domains and discipline tab this session left
    // persisted in localStorage. Cleared by key directly (rather than via
    // WorkspaceToolkitService.clearPersistedState()) so this — an eagerly
    // loaded, app-wide service — doesn't statically pull in that much
    // larger, icon-heavy service into the main bundle.
    localStorage.removeItem(SELECTED_DOMAINS_STORAGE_KEY);
    localStorage.removeItem(DISCIPLINE_TAB_STORAGE_KEY);
  }

  private decodeUserFromToken(token: string): UserInfo {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  }

  private isTokenExpired(token: string | null): boolean {
    if (!token) {
      return true;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (!payload.exp) {
        return false;
      }
      return Date.now() >= payload.exp * 1000;
    } catch {
      return true;
    }
  }
}
